import { useContext, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AcademicCapIcon,
  ArrowRightIcon,
  BookOpenIcon,
  BriefcaseIcon,
  CheckBadgeIcon,
  ClipboardDocumentCheckIcon,
  DocumentCheckIcon,
  LightBulbIcon,
  LockClosedIcon,
  MicrophoneIcon,
  SparklesIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { UserContext } from '../../utils/contexts/UserContext';

const guideContent = {
  student: {
    eyebrow: 'Student guide',
    title: 'Build a portfolio that grows with your evidence',
    intro: 'Keep your profile accurate, complete assigned interviews, and decide which AI-extracted evidence facts become part of your portfolio.',
    startPath: '/portfolio',
    startLabel: 'Open my portfolio',
    quickStart: [
      'Complete your profile and add a clear headline.',
      'Add education, work, activities, and projects—even if you do not have every optional detail.',
      'Attach supporting evidence and review the AI extraction before publishing it.',
      'Complete assigned interviews and wait for expert approval before treating results as final.',
    ],
    sections: [
      {
        title: 'Create your portfolio',
        icon: BookOpenIcon,
        description: 'Your portfolio combines facts you enter, confirmed evidence, and approved interview results.',
        steps: [
          'Open Portfolio and use the appropriate section to add education, experience, an activity, or a project.',
          'Only the core entry information is needed. School, location, URL, title, and detailed project or experience fields may be left blank when they do not apply.',
          'Use the date picker when dates are known and keep descriptions factual.',
        ],
        link: ['/portfolio', 'Manage portfolio'],
      },
      {
        title: 'Prove an activity with evidence',
        icon: DocumentCheckIcon,
        description: 'Evidence remains private and does not update your profile until you confirm the analysis.',
        steps: [
          'After saving an entry, follow its evidence action to upload a file, certificate, image, recording, video, or public HTTPS link.',
          'Wait while AI extracts facts, source excerpts, and possible skills. Uploaded content is treated as evidence—not as instructions to the AI.',
          'Correct factual fields, accept or reject each skill suggestion, then confirm and publish. Reject the analysis if it is not reliable.',
          'Removing confirmed evidence also removes its portfolio contribution while retaining non-content audit information.',
        ],
        link: ['/portfolio#evidence', 'Open evidence library'],
      },
      {
        title: 'Complete an interview',
        icon: MicrophoneIcon,
        description: 'Interviews provide structured evidence of how you explain and apply your skills.',
        steps: [
          'Open Interviews and select an available assignment from your organization.',
          'Check your microphone, answer in your own words, and complete any follow-up questions.',
          'After submission, the result remains pending while AI prepares a draft and a human expert reviews it.',
          'Only approved summaries and interview characteristics appear as authoritative results in your portfolio.',
        ],
        link: ['/my-interviews', 'View my interviews'],
      },
      {
        title: 'Understand your skills',
        icon: SparklesIcon,
        description: 'The portfolio keeps different kinds of conclusions visibly separate.',
        steps: [
          'Expert-approved interview skills include reviewed scores and rationales.',
          'Evidence-based skills are suggestions you confirmed and show confidence and provenance; they do not change interview scores.',
          'Retired skills stay visible under Historical skills so earlier achievements are not lost.',
          'Your overview is rebuilt from approved interview results and confirmed evidence, with source information and an update date.',
        ],
      },
    ],
    note: 'You can access your own portfolio and results. Draft evaluations, expert prompts, private annotations, and other students’ information are not available to you.',
  },
  expert: {
    eyebrow: 'Expert guide',
    title: 'Turn AI drafts into accountable human-reviewed evaluations',
    intro: 'Review only assigned interviews, ground conclusions in the student’s responses, and approve a revision only when it is ready to become authoritative.',
    startPath: '/expert/reviews',
    startLabel: 'Open review queue',
    quickStart: [
      'Open an assigned session from the review queue.',
      'Inspect the transcript, audio, follow-ups, predefined skills, and cited evidence.',
      'Add precise answer-level notes and request a revised evaluation when needed.',
      'Approve only the latest successful revision after checking its complete effect.',
    ],
    sections: [
      {
        title: 'Work from your assigned queue',
        icon: ClipboardDocumentCheckIcon,
        description: 'Your queue contains sessions from interviews currently assigned to you.',
        steps: [
          'Use student, interview, date, and status filters to find work that needs attention.',
          'Open the review workspace and confirm that the session context matches the assignment.',
          'Use Assigned portfolios for contextual information that your interview assignments permit you to see.',
        ],
        link: ['/expert/reviews', 'Open review queue'],
      },
      {
        title: 'Review the complete response',
        icon: MicrophoneIcon,
        description: 'Scores and rationales should be supported by what the student actually said.',
        steps: [
          'Listen to available audio and compare it with the primary transcript and follow-up responses.',
          'Check each AI rationale, score, supporting excerpt, and mapped predefined skill.',
          'Use concise professional notes to explain inaccuracies, missing context, or the interpretation you expect.',
          'Do not include unnecessary sensitive information or unsupported assumptions in annotations.',
        ],
      },
      {
        title: 'Request a better revision',
        icon: LightBulbIcon,
        description: 'A re-evaluation command guides the next draft without erasing the audit history.',
        steps: [
          'Save answer-level annotations before starting re-evaluation.',
          'Write a session-level command that names the desired correction, evidence standard, or emphasis.',
          'Generate one revision at a time and monitor its status. A failed draft never replaces an approved result.',
          'Compare revision history and verify that evidence excerpts occur in the referenced transcript.',
        ],
      },
      {
        title: 'Approve and publish',
        icon: CheckBadgeIcon,
        description: 'Approval is the human decision that makes an evaluation visible and updates student characteristics.',
        steps: [
          'Approve only the latest successful revision after reviewing per-answer evaluations, consolidated skills, and the overall summary.',
          'Approval updates the session summary and approved skill assessments, then recalculates cumulative student skill information.',
          'If a later revision is generated, the previously approved result remains published until the new revision is approved.',
        ],
      },
    ],
    note: 'Your access is limited to actively assigned interviews. You cannot browse unrelated students or publish unreviewed drafts.',
  },
  organization: {
    eyebrow: 'Organization administrator guide',
    title: 'Coordinate interviews, experts, students, and review progress',
    intro: 'Set up clear interview workflows, assign the right experts, monitor evaluation status, and help students understand what happens next.',
    startPath: '/organization/dashboard',
    startLabel: 'Open organization dashboard',
    quickStart: [
      'Complete organization settings and review dashboard activity.',
      'Create an interview, add questions and skills, then create student sessions.',
      'Invite active experts and assign them to the appropriate interviews.',
      'Monitor reports and review status without approving on an expert’s behalf.',
    ],
    sections: [
      {
        title: 'Set up the organization',
        icon: BriefcaseIcon,
        description: 'Organization settings establish the context students and experts work within.',
        steps: [
          'Use the dashboard to review students, interviews, recent activity, and pending review work.',
          'Keep organization details accurate in Settings and use portfolio links to inspect same-organization student information read-only.',
          'Remember that platform-wide service access and limits are controlled by the superadmin.',
        ],
        link: ['/organization/settings', 'Open settings'],
      },
      {
        title: 'Create and publish interviews',
        icon: AcademicCapIcon,
        description: 'An interview connects questions, managed skills, students, and human review.',
        steps: [
          'Create an interview and explain its purpose clearly.',
          'Add existing questions from the question bank or create the questions you need, then confirm their associated skills.',
          'Use administrator preview mode to test the student experience without creating a real student evaluation.',
          'Create sessions for the intended students and check session progress from the interview workspace.',
        ],
        link: ['/organization/interviews', 'Manage interviews'],
      },
      {
        title: 'Invite and assign experts',
        icon: UserGroupIcon,
        description: 'Experts need an active account and an interview assignment before they can review sessions.',
        steps: [
          'Invite an expert using a valid work email. If delivery fails, correct SMTP credentials and resend the invitation.',
          'Deactivate access that is no longer needed and keep assignments limited to relevant interviews.',
          'Assign at least one expert before students complete an interview so review ownership is clear.',
        ],
        link: ['/organization/experts', 'Manage experts'],
      },
      {
        title: 'Monitor reports and reviews',
        icon: ClipboardDocumentCheckIcon,
        description: 'Organization administrators can inspect progress and drafts but cannot provide expert approval.',
        steps: [
          'Use Reports to see submitted sessions, including results still awaiting expert evaluation or approval.',
          'Status labels distinguish generation, review, failure, and approval states.',
          'Open Reviews to inspect the workspace and history. The assigned expert remains responsible for final approval.',
          'Use published results for reporting only after the approval state is visible.',
        ],
        link: ['/organization/reviews', 'Monitor reviews'],
      },
    ],
    note: 'Organization administrators may inspect same-organization data and review drafts, but only an assigned expert can approve an evaluation.',
  },
};

const audienceLabels = {
  organization: 'Organization',
  student: 'Student',
  expert: 'Expert',
};

function GuideSection({ section, index }) {
  const Icon = section.icon;
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="flex items-start gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-200">
          <Icon className="h-6 w-6" aria-hidden="true" />
        </span>
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Step {index + 1}</p>
          <h2 className="mt-1 text-xl font-bold text-slate-950 dark:text-white">{section.title}</h2>
          <p className="mt-2 leading-7 text-slate-600 dark:text-slate-300">{section.description}</p>
        </div>
      </div>
      <ol className="mt-5 space-y-3 border-t border-slate-100 pt-5 dark:border-slate-700">
        {section.steps.map((step, stepIndex) => (
          <li key={step} className="flex gap-3 text-sm leading-6 text-slate-700 dark:text-slate-200">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-700 dark:bg-slate-700 dark:text-white">{stepIndex + 1}</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
      {section.link && <Link to={section.link[0]} className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-blue-700 hover:underline dark:text-blue-300">{section.link[1]}<ArrowRightIcon className="h-4 w-4" aria-hidden="true" /></Link>}
    </article>
  );
}

export default function UserGuide() {
  const auth = useContext(UserContext) || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const canChooseAudience = Boolean(auth.isUniversity || auth.isAdmin);
  const forcedAudience = auth.isStudent ? 'student' : auth.isExpert ? 'expert' : null;
  const requestedAudience = searchParams.get('audience');
  const selectedAudience = forcedAudience || (canChooseAudience && guideContent[requestedAudience] ? requestedAudience : 'organization');
  const guide = guideContent[selectedAudience];

  useEffect(() => {
    document.title = `${audienceLabels[selectedAudience]} user guide | Smash Index`;
  }, [selectedAudience]);

  const chooseAudience = (audience) => {
    if (canChooseAudience) setSearchParams({ audience });
  };

  return (
    <main className="mx-auto max-w-7xl px-4 py-8 md:px-8">
      <header className="overflow-hidden rounded-3xl bg-gradient-to-br from-slate-950 via-blue-950 to-blue-700 px-6 py-10 text-white shadow-xl sm:px-10">
        <div className="max-w-4xl">
          <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-blue-200"><BookOpenIcon className="h-5 w-5" aria-hidden="true" />{guide.eyebrow}</p>
          <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-5xl">{guide.title}</h1>
          <p className="mt-5 max-w-3xl text-base leading-7 text-blue-100 sm:text-lg">{guide.intro}</p>
          <Link to={guide.startPath} className="mt-7 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 font-bold text-blue-800 shadow-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-800">{guide.startLabel}<ArrowRightIcon className="h-4 w-4" aria-hidden="true" /></Link>
        </div>
      </header>

      {canChooseAudience && (
        <section className="mt-8" aria-labelledby="audience-heading">
          <h2 id="audience-heading" className="text-sm font-bold uppercase tracking-wider text-slate-500">Choose a guide</h2>
          <div className="mt-3 inline-flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm dark:border-slate-700 dark:bg-slate-800" role="tablist" aria-label="User guide audience">
            {Object.keys(audienceLabels).map((audience) => (
              <button key={audience} type="button" role="tab" aria-selected={selectedAudience === audience} onClick={() => chooseAudience(audience)} className={`rounded-xl px-4 py-2.5 text-sm font-bold transition ${selectedAudience === audience ? 'bg-blue-700 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700'}`}>{audienceLabels[audience]}</button>
            ))}
          </div>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-500">Use these role-specific guides when onboarding administrators, students, and experts in your organization.</p>
        </section>
      )}

      <div className="mt-8 grid gap-7 lg:grid-cols-[minmax(0,1fr)_320px]">
        <section className="grid gap-5 md:grid-cols-2" aria-label={`${audienceLabels[selectedAudience]} workflow`}>
          {guide.sections.map((section, index) => <GuideSection key={section.title} section={section} index={index} />)}
        </section>
        <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
          <section className="rounded-2xl border border-blue-100 bg-blue-50 p-5 dark:border-blue-900 dark:bg-blue-950/50">
            <h2 className="font-bold text-blue-950 dark:text-blue-100">Quick-start checklist</h2>
            <ul className="mt-4 space-y-3">
              {guide.quickStart.map((item) => <li key={item} className="flex gap-2.5 text-sm leading-6 text-blue-950 dark:text-blue-100"><CheckBadgeIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-700 dark:text-blue-300" aria-hidden="true" /><span>{item}</span></li>)}
            </ul>
          </section>
          <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-center gap-2"><LockClosedIcon className="h-5 w-5 text-emerald-600" aria-hidden="true" /><h2 className="font-bold">Access and privacy</h2></div>
            <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">{guide.note}</p>
          </section>
        </aside>
      </div>
    </main>
  );
}
